import { Loader2, Mic, RotateCcw, Send, Volume2, VolumeX } from "lucide-react";
import { API } from "@/api";
import { GoldImage } from "../GoldImage";

/* Pannello conversazione del Concierge: banner fotografico reale, messaggi in
 * streaming, azioni rapide, input con dettatura vocale. Tutta la logica (SSE,
 * Whisper, SpeechSynthesis) resta nel genitore: qui solo presentazione. */
export const ChatPanel = ({ tc, messages, busy, input, setInput, send, recording, transcribing, toggleRec, voiceOn, toggleVoice, reset, scrollRef }) => (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-amber-500/25 bg-[#0b0b0c]/90" data-testid="concierge-chat">
        {/* Banner con fotografia reale (site-media con fallback bundled) */}
        <div className="relative h-40 shrink-0 sm:h-48">
            <GoldImage src={`${API}/public/site-media/hero-rome.jpg`} alt="Roma" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-black/55 to-black/30" />
            <div className="absolute right-3 top-3 flex items-center gap-3 rounded-full border border-amber-500/20 bg-black/50 px-3 py-1.5 backdrop-blur-sm">
                <button onClick={toggleVoice} data-testid="concierge-voice-toggle" aria-label={voiceOn ? tc.voiceOn : tc.voiceOff} title={voiceOn ? tc.voiceOn : tc.voiceOff}
                    className={`transition-colors ${voiceOn ? "text-amber-300" : "text-stone-400 hover:text-amber-300"}`}>
                    {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <button onClick={reset} data-testid="concierge-reset" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-400 transition-colors hover:text-amber-300">
                    <RotateCcw size={11} /> {tc.reset}
                </button>
            </div>
            <div className="absolute bottom-3 left-4 right-4 flex items-end gap-3.5">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-amber-400/70 bg-[#0b0b0c]/80 shadow-[0_0_20px_rgba(212,175,55,0.35)]" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 64 64">
                        <circle cx="32" cy="32" r="26" fill="none" stroke="#D4AF37" strokeWidth="2" opacity="0.6" />
                        <path d="M32 10 L37 32 L32 54 L27 32 Z" fill="#F1D36C" />
                        <path d="M10 32 L32 27 L54 32 L32 37 Z" fill="#D4AF37" opacity="0.55" />
                        <circle cx="32" cy="32" r="3.5" fill="#F1D36C" />
                    </svg>
                </span>
                <div className="min-w-0">
                    <p className="text-[9px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Tourvoyagers AI Concierge</p>
                    <p className="mt-1 font-display text-sm leading-snug text-amber-50 sm:text-base" data-testid="concierge-greeting">{tc.greeting}</p>
                </div>
            </div>
        </div>

        {/* Azioni rapide: inviano messaggi reali alla conversazione */}
        {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 border-b border-amber-500/10 px-4 py-3" data-testid="concierge-quick-actions">
                {tc.quickActions.map((label, i) => (
                    <button key={label} data-testid={`concierge-quick-${i}`} onClick={() => send(tc.quickMsgs[i])}
                        className="rounded-full border border-amber-500/30 bg-amber-400/5 px-3.5 py-1.5 text-[11px] text-amber-200/90 transition-colors hover:bg-amber-400/15">
                        {label}
                    </button>
                ))}
            </div>
        )}

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5" style={{ minHeight: 300, maxHeight: 420 }} data-testid="concierge-messages">
            {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${m.role === "user"
                        ? "bg-amber-400 text-black rounded-br-sm"
                        : `bg-[#161618] border border-amber-500/15 text-stone-200 rounded-bl-sm ${m.isError ? "!border-red-400/40" : ""}`}`}
                        data-testid={`concierge-msg-${m.role}`}>
                        {m.text}
                        {m.streaming && !m.text && <span className="inline-flex items-center gap-2 text-stone-500 text-xs"><Loader2 size={12} className="animate-spin" />{tc.thinking}</span>}
                        {m.streaming && m.text && <span className="ml-1 inline-block h-3.5 w-1.5 animate-pulse bg-amber-400 align-middle" />}
                    </div>
                </div>
            ))}
        </div>

        {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-4 pb-2 sm:px-5">
                {tc.chips.map((c) => (
                    <button key={c} data-testid="concierge-chip" onClick={() => send(c)}
                        className="rounded-full border border-amber-500/20 px-3 py-1 text-[10px] text-stone-400 transition-colors hover:border-amber-400/40 hover:text-amber-200">
                        {c}
                    </button>
                ))}
            </div>
        )}

        <div className="border-t border-amber-500/15 p-4">
            {recording && <p className="mb-2 text-[11px] text-red-300 animate-pulse" data-testid="concierge-listening">● {tc.listening}</p>}
            {transcribing && <p className="mb-2 text-[11px] text-amber-300" data-testid="concierge-transcribing">{tc.transcribing}</p>}
            <div className="flex items-center gap-2.5">
                <input
                    data-testid="concierge-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={tc.placeholder}
                    className="input-lux flex-1"
                />
                <button type="button" onClick={toggleRec} disabled={transcribing} data-testid="concierge-mic"
                    aria-label={recording ? tc.listening : tc.placeholder}
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-all ${recording ? "border-red-400 bg-red-400/15 text-red-300 animate-pulse" : "border-amber-500/40 text-amber-200 hover:bg-amber-400/10"}`}>
                    {transcribing ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
                </button>
                <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="concierge-send"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 text-black transition-all hover:bg-amber-300 disabled:opacity-40">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
            </div>
        </div>
    </section>
);
